<template>
  <div class="marketing-html-page text-gray-800 antialiased overflow-x-hidden bg-slate-50 min-h-screen">
    <MarketingNav variant="faq" :navbar-scrolled="navbarScrolled" />

    <header class="bg-slate-900 pt-40 pb-16 text-center text-white border-b-4 border-green-500 relative overflow-hidden">
      <div class="absolute inset-0 w-full h-full bg-gradient-to-tr from-green-500/20 to-blue-500/20 opacity-30" />
      <div class="relative z-10 max-w-4xl mx-auto px-4">
        <h1 class="text-4xl md:text-5xl font-black mb-4 tracking-tight">Centro de Ayuda</h1>
        <p class="text-slate-400 text-lg font-medium">¿En qué podemos ayudarte? Encuentra respuestas rápidas a todas tus dudas.</p>
      </div>
    </header>

    <div class="sticky top-20 z-40 bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100 transition-all duration-300 pt-6 pb-3">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav class="flex space-x-6 overflow-x-auto hide-scrollbar items-center pb-2">
          <a
            v-for="nav in navEntries"
            :key="nav.id"
            :href="'#' + nav.id"
            class="nav-category whitespace-nowrap text-sm font-semibold text-slate-500 hover:text-green-500 transition"
            :class="{ active: activeNav === nav.id }"
            @click.prevent="scrollToSection(nav.id)"
          >{{ nav.label }}</a>
        </nav>
      </div>
    </div>

    <main class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 py-16">
      <div
        v-for="sec in faqSections"
        :id="sec.id"
        :key="sec.id"
        class="scroll-mt-48"
      >
        <h2 class="text-2xl font-black text-slate-900 mb-6 flex items-center">
          <span
            class="w-10 h-10 rounded-xl flex items-center justify-center mr-3 shadow-sm"
            :class="sec.chip"
          >{{ sec.emoji }}</span>
          {{ sec.title }}
        </h2>
        <div class="space-y-4">
          <div
            v-for="item in sec.items"
            :key="item.id"
            class="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer faq-item hover:shadow-md transition-all duration-300"
            @click="toggleFaq(item.id)"
          >
            <div class="px-6 md:px-8 py-5 flex justify-between items-center">
              <h4 class="text-lg font-bold text-slate-800 pr-4">{{ item.q }}</h4>
              <svg
                class="w-6 h-6 flex-shrink-0 faq-icon"
                :class="['text-' + sec.icon, { open: openFaqId === item.id }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <div
              class="px-6 md:px-8 faq-content text-slate-600 leading-relaxed border-t faq-text"
              :class="openFaqId === item.id ? 'border-slate-100 open' : 'border-transparent'"
              v-html="item.body"
            />
          </div>
        </div>
      </div>
    </main>

    <MarketingFooter />
  </div>
</template>

<script>
import marketingPage from './mixins/marketingPage'
import MarketingNav from './components/MarketingNav.vue'
import MarketingFooter from './components/MarketingFooter.vue'
import faqSections from './faqContent.json'

const NAV_ENTRIES = [
  { id: 'solicitud', label: '📝 Solicitud' },
  { id: 'aprobacion', label: '✅ Aprobación' },
  { id: 'pagos', label: '💸 Pagos y Límites' },
  { id: 'seguridad', label: '🛡️ Seguridad y App' }
]

export default {
  name: 'FaqHtmlPage',
  components: { MarketingNav, MarketingFooter },
  mixins: [marketingPage],
  data() {
    return {
      faqSections,
      navbarScrolled: false,
      openFaqId: null,
      activeNav: 'solicitud',
      navEntries: NAV_ENTRIES
    }
  },
  watch: {
    '$route.hash': 'applyRouteHash'
  },
  mounted() {
    this._onScroll = () => {
      this.navbarScrolled = window.scrollY > 50
      this.updateActiveNav()
    }
    window.addEventListener('scroll', this._onScroll, { passive: true })
    this._onScroll()
    this.$nextTick(() => {
      this.applyRouteHash()
    })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll)
  },
  methods: {
    toggleFaq(id) {
      this.openFaqId = this.openFaqId === id ? null : id
    },
    scrollToSection(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    updateActiveNav() {
      const y = window.scrollY
      let current = this.navEntries[0].id
      this.navEntries.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el && y >= el.offsetTop - 220) current = id
      })
      this.activeNav = current
    },
    applyRouteHash() {
      const raw = (this.$route.hash || '').replace(/^#/, '')
      if (!raw) return
      const base = raw.split('&')[0]
      if (this.navEntries.some((n) => n.id === base)) {
        this.$nextTick(() => this.scrollToSection(base))
      }
    }
  }
}
</script>

<style scoped>
.marketing-html-page {
  scroll-behavior: smooth;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.faq-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.3s ease, padding 0.3s ease;
  opacity: 0;
}
.faq-content.open {
  max-height: 1000px;
  opacity: 1;
  padding-top: 1rem;
  padding-bottom: 1.5rem;
}
.faq-icon {
  transition: transform 0.3s ease;
}
.faq-item svg.faq-icon.open,
.faq-icon.open {
  transform: rotate(180deg);
}
.nav-category {
  position: relative;
}
.nav-category::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #22c55e;
  transform: scaleX(0);
  transition: transform 0.3s ease;
  border-radius: 2px;
}
.nav-category.active {
  color: #22c55e;
  font-weight: 700;
}
.nav-category.active::after {
  transform: scaleX(1);
}
.faq-text >>> ul {
  list-style-type: disc;
  padding-left: 1.25rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.faq-text >>> ol {
  list-style-type: decimal;
  padding-left: 1.25rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.faq-text >>> li {
  margin-bottom: 0.25rem;
}
</style>
